/**
 * Symbology & cartography algorithms — spec Part G (`lib/symbology.ts`, the reference
 * implementation).
 *
 * §G1 (`quantBreaks` / `equalBreaks` / `classOf`), §G2 (`lerpHex` / `sampleRamp`) and §G4
 * (`OV_POOL` / `near` / `overlayColor`) are
 * copied **verbatim** from the spec, because they are the shared contract with the future
 * Python port (`apps/analysis/symbology.py`, plan Global Constraints): the browser legend,
 * a server-rendered legend and an exported map must land on the same class boundaries and
 * the same hex strings. Do not "improve" them — in particular `quantBreaks` indexes with
 * `Math.floor(i * n / k)` (not a interpolating quantile) and `classOf` uses a strict `>`,
 * so the lowest class is `≤ b0`. Both behaviours are asserted in `symbology.test.ts`.
 *
 * Everything below the two verbatim blocks (`autoRamp`, `legendLabels`, `classify`) is
 * §G2's "Auto ramp per indicator" sentence and §G3's prose expressed as code.
 *
 * The only edits to the spec's own text are seven `!` non-null assertions on array indexing,
 * the `export` on `lerpHex`/`near`, and line wrapping: this project compiles with
 * `noUncheckedIndexedAccess`, under which `s[i]` is `number | undefined`. The assertions are
 * type-level only — every index is in range by construction (`i < k`,
 * `lo <= ramp.length - 1`, `i < overlays.length`, `i < 2 <= OV_POOL.length`) — so the
 * emitted JavaScript is character-for-character the spec's. NOTES #188.
 */
import { fmtNum } from './format';

/* ------------------------------------------------------------------ §G1 verbatim */

export function quantBreaks(vals: number[], k = 5): number[] {
  const s = [...vals].sort((a, b) => a - b);
  return Array.from({ length: k - 1 }, (_, i) => s[Math.floor(((i + 1) * s.length) / k)]!);
}

export function equalBreaks(vals: number[], k = 5): number[] {
  const mn = Math.min(...vals),
    mx = Math.max(...vals);
  return Array.from({ length: k - 1 }, (_, i) => mn + ((i + 1) * (mx - mn)) / k);
}

export const classOf = (v: number, breaks: number[]) =>
  breaks.reduce((c, b) => (v > b ? c + 1 : c), 0);

/* ------------------------------------------------------------------ §G2 verbatim */

/**
 * §G2 declares this `function lerpHex(...)` (module-private). It is exported here so the
 * ramp maths can be unit-tested against known midpoints without going through
 * `sampleRamp`; the body is unchanged.
 */
export function lerpHex(a: string, b: string, t: number): string {
  const pa = [1, 3, 5].map((i) => parseInt(a.slice(i, i + 2), 16));
  const pb = [1, 3, 5].map((i) => parseInt(b.slice(i, i + 2), 16));
  return (
    '#' +
    pa.map((v, i) => Math.round(v + (pb[i]! - v) * t).toString(16).padStart(2, '0')).join('')
  );
}

export function sampleRamp(ramp: string[], k: number): string[] {
  if (k === ramp.length) return [...ramp];
  return Array.from({ length: k }, (_, i) => {
    const pos = (i * (ramp.length - 1)) / Math.max(1, k - 1);
    const lo = Math.floor(pos),
      t = pos - lo;
    return t === 0 ? ramp[lo]! : lerpHex(ramp[lo]!, ramp[Math.min(ramp.length - 1, lo + 1)]!, t);
  });
}

/* -------------------------------------------------------- §G2 auto ramp per indicator */

/** §G2: "domain map {gesi:plum, water:blue, …}". Keys are `/meta/config.domains[].id`. */
export const DOMAIN_RAMP: Record<string, string> = {
  gesi: 'plum',
  water: 'blue',
  landuse: 'green',
  soil: 'brown',
  climate: 'ochre',
  ecosys: 'ndvi',
  slm: 'green',
  digital: 'slate',
};

/**
 * §G2: "overrides: rainfall|spi→div, npk→divg" — three indicators whose values are
 * anomalies/balances around a meaningful zero, so they need a diverging ramp regardless
 * of the domain they sit in. Keyed by `indicators.id`.
 */
export const INDICATOR_RAMP: Record<string, string> = {
  rainfall: 'div',
  spi: 'div',
  npk: 'divg',
};

/** Ramp used when a domain is unknown — the mockup's `|| RAMPS.green`. */
export const FALLBACK_RAMP_KEY = 'green';

/**
 * §G2 "Auto ramp per indicator". Returns a **key** into `/meta/config.ramps` rather than
 * the stops themselves, so the caller resolves it against the server's palette (§F1: the
 * ramps are server config, not a client constant).
 */
export function autoRamp(indicatorId: string, domainId: string): string {
  return INDICATOR_RAMP[indicatorId] ?? DOMAIN_RAMP[domainId] ?? FALLBACK_RAMP_KEY;
}

/* -------------------------------------------------- §G4 overlay auto-contrast colours */

/** §F1 `AppState.overlays[i]`. Re-declared structurally so this module imports no store. */
export interface Overlay {
  id: string;
  color?: string;
}

export const OV_POOL = ['#2B6CB0', '#8E3B62', '#C77B21', '#22301F'];

export const near = (
  a: string,
  b: string, // cheap RGB distance
) =>
  [1, 3, 5].reduce(
    (s, i) => s + Math.abs(parseInt(a.slice(i, i + 2), 16) - parseInt(b.slice(i, i + 2), 16)),
    0,
  ) < 120;

export function overlayColor(i: number, overlays: Overlay[], fillRampLastStop: string): string {
  if (overlays[i]!.color) return overlays[i]!.color!; // user override wins
  const taken = [
    fillRampLastStop,
    ...overlays.filter((o, j) => j !== i && o.color).map((o) => o.color!),
  ];
  const free = OV_POOL.filter((c) => !taken.some((t) => near(c, t)));
  return free[i % free.length] ?? OV_POOL[i]!;
}

/**
 * §G4's closing line: "Symbol radius: `r = base + √t · span`, base/span = 10/26 at country
 * level else 5/15; t = (v−min)/(max−min) within the displayed set." Also §F5.5's
 * `(lvl===0?10:5) + √t · (lvl===0?26:15)`.
 *
 * `t` is clamped to [0,1]: a single-valued displayed set makes `(v−min)/(max−min)` a NaN,
 * and a NaN radius is an invisible marker — the mockup guards it at the call site
 * (`mx === mn ? .5 : …`), and this guard is the backstop for every other caller.
 */
export function symbolRadius(t: number, level: number): number {
  const base = level === 0 ? 10 : 5;
  const span = level === 0 ? 26 : 15;
  const clamped = Number.isFinite(t) ? Math.min(1, Math.max(0, t)) : 0;
  return base + Math.sqrt(clamped) * span;
}

/* ------------------------------------------------------------------ §G5 bivariate */

/**
 * §G5: "Tertiles per variable (`quantBreaks(vals,3)`); colour `BIV[yClass][xClass]` with the
 * Part A matrix; labels `['low','medium','high']`."
 */
export const BIV_K = 3;

export const BIV_LABELS = ['low', 'medium', 'high'] as const;

export type BivLabel = (typeof BIV_LABELS)[number];

/**
 * The Part A matrix (the mockup's `const BIV` — a Stevens-style green × blue scheme),
 * indexed `[yClass][xClass]`: row 0 is low Y, column 0 is low X, so `[0][0]` is the neutral
 * grey "low on both" and `[2][2]` the dark teal "high on both".
 *
 * The authority is `/meta/config.biv` (§D1), which serves exactly these nine strings; this
 * copy is the fallback for the same reason `FALLBACK_RAMP_STOPS` is one — the paint must not
 * depend on a config round trip having landed, and a bivariate map with a missing matrix
 * would be nine undefined fills.
 */
export const FALLBACK_BIV: string[][] = [
  ['#E8E8E8', '#B8D6BE', '#73AE80'],
  ['#B5C0DA', '#90B2B3', '#5A9178'],
  ['#6C83B5', '#567994', '#2A5A5B'],
];

export interface BivariateBreaks {
  /** Two upper bounds splitting X into tertiles; feed to `classOf`. */
  xBreaks: number[];
  /** Same for Y. */
  yBreaks: number[];
}

/**
 * §G5's "tertiles per variable". The two variables are classified **independently** — that
 * is the whole point of a bivariate scheme: each axis ranks the units against its own
 * distribution, and the colour is the crossing of the two ranks.
 */
export function bivariateClasses(xVals: number[], yVals: number[]): BivariateBreaks {
  return { xBreaks: quantBreaks(xVals, BIV_K), yBreaks: quantBreaks(yVals, BIV_K) };
}

/**
 * §G5 `BIV[yClass][xClass]`. Indices are clamped to 0–2 rather than trusted: `classOf`
 * cannot return out of range for two breaks, but a matrix served short (or a caller passing
 * a k≠3 class) would otherwise paint `undefined`, which Leaflet renders as black.
 */
export function bivColor(xc: number, yc: number, biv: string[][] = FALLBACK_BIV): string {
  const clamp = (i: number) =>
    Math.min(BIV_K - 1, Math.max(0, Number.isFinite(i) ? Math.trunc(i) : 0));
  const y = clamp(yc);
  const x = clamp(xc);
  return biv[y]?.[x] ?? FALLBACK_BIV[y]![x]!;
}

/** §G5 "labels `['low','medium','high']`" — the tertile a class index names. */
export const bivLabel = (c: number): BivLabel =>
  BIV_LABELS[Math.min(BIV_K - 1, Math.max(0, Math.trunc(c)))]!;

/* --------------------------------------------------------------- §G3 legend labels */

export type LegendMode = 'values' | 'classes';

/**
 * §G3 "Classes mode by k". 6 and 7 share the generated form (`Lowest`, `Class 2` …
 * `Class k-1`, `Highest`), which is also what any k outside 3–5 gets.
 */
export function classLabels(k: number): string[] {
  if (k === 3) return ['Low', 'Medium', 'High'];
  if (k === 4) return ['Low', 'Medium-low', 'Medium-high', 'High'];
  if (k === 5) return ['Very low', 'Low', 'Medium', 'High', 'Very high'];
  return Array.from({ length: k }, (_, i) =>
    i === 0 ? 'Lowest' : i === k - 1 ? 'Highest' : `Class ${i + 1}`,
  );
}

/**
 * §G3 legend row labels for `k` classes built on `breaks` (length `k - 1`).
 *
 * Values mode: `≤ b0`, `b0 – b1`, …, `> b(k-2)`, every number through `fmtNum` so it
 * carries the indicator's `fmt` decimals *and* thousands separators (§G8: "counts
 * formatted with separators"). The dash is an en dash with hair spaces, as the mockup
 * renders it. Classes mode: `classLabels(k)`.
 */
export function legendLabels(
  breaks: number[],
  fmt: number,
  mode: LegendMode = 'values',
  k: number = breaks.length + 1,
): string[] {
  if (mode === 'classes') return classLabels(k);
  return Array.from({ length: k }, (_, i) => {
    const lo = i === 0 ? null : (breaks[i - 1] ?? null);
    const hi = i === k - 1 ? null : (breaks[i] ?? null);
    if (lo === null) return `≤ ${fmtNum(hi, fmt)}`;
    if (hi === null) return `> ${fmtNum(lo, fmt)}`;
    return `${fmtNum(lo, fmt)} – ${fmtNum(hi, fmt)}`;
  });
}

/* ------------------------------------------------------------------ classify helper */

/** §F1 `AppState.indStyle`. Re-declared structurally so this module imports no store. */
export interface IndStyle {
  ramp: string | null;
  method: 'quantile' | 'equal';
  k: number;
  opacity: number;
}

export interface Classification {
  /** `k - 1` upper bounds; feed to `classOf`. */
  breaks: number[];
  /** `k` hex colours, index-aligned with `classOf`'s return value. */
  colors: string[];
}

/**
 * Green, from the mockup's `RAMPS` — only used when `/meta/config.ramps` is unavailable.
 * Exported because §G4's `fillRampLastStop` needs a last stop even before the config lands
 * (and when no indicator is mapped at all): the mockup's `rampFamilyColor` falls back to
 * `RAMPS.green` for exactly that case.
 */
export const FALLBACK_RAMP_STOPS = ['#EFF6EE', '#C9E2C8', '#94C595', '#57A05D', '#2E7D32'];

/**
 * One call for the whole §F5.3 paint: pick the breaks per `indStyle.method` and resample
 * `rampStops` to `indStyle.k`. `rampStops` is the *resolved* 5-stop ramp — the caller has
 * already applied `indStyle.ramp ?? autoRamp(indicator, domain)` against
 * `/meta/config.ramps`, which keeps this function pure and portable.
 */
export function classify(
  values: number[],
  indStyle: IndStyle,
  rampStops: string[] = FALLBACK_RAMP_STOPS,
): Classification {
  const k = indStyle.k;
  const breaks =
    indStyle.method === 'equal' ? equalBreaks(values, k) : quantBreaks(values, k);
  return { breaks, colors: sampleRamp(rampStops, k) };
}

/* ------------------------------------------------------- §F5.6(d) raster ramp strip */

/**
 * The `rescale=min,max` a §D5 `tile_template` carries, as the numbers TiTiler stretches
 * the ramp between — i.e. the low/high labels of the legend's ramp strip.
 *
 * Returns `null` when the template has no `rescale` (TiTiler then stretches per-tile from
 * the band's own statistics, so there is no single pair of numbers the legend could
 * honestly print, and §F5.6 falls back to "low"/"high").
 *
 * Parsed off the string rather than off `URLSearchParams`: the template is not a valid URL
 * (it opens with `/tiles/…` and contains unencoded `{z}` placeholders), and the §I2 nginx
 * allow-list forbids percent-encoding inside `/tiles/`, so `rescale`'s comma is always
 * literal (Task 2, NOTES #247).
 */
export function parseRescale(template: string | undefined | null): [number, number] | null {
  if (!template) return null;
  const m = /[?&]rescale=(-?[\d.]+),(-?[\d.]+)/.exec(template);
  if (!m) return null;
  const lo = Number(m[1]);
  const hi = Number(m[2]);
  if (!Number.isFinite(lo) || !Number.isFinite(hi)) return null;
  return [lo, hi];
}
